import React from "react"
import { TFunction, useTranslation } from "react-i18next"
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from "recoil"
import { ConnectUser } from "../../api/ConnectApi"
import Modal from "../Modal"
import {
  addSnackSelector,
  projectIdState,
  projectRegionState,
  projectUsersState,
  quadriProjectIdState,
  userGroupsState,
  usersByTidUuidState
} from "../StateSelectors"
import { useMutation, useQuery } from "@apollo/client"
import { GET_PROJECT_ACCESS, UPDATE_PROJECT_ACCESS } from "../../api/QuadriApi"
import { SnackProps } from "./SnackBars"
import Skeleton from "react-loading-skeleton"
import "react-loading-skeleton/dist/skeleton.css"

interface UserPermissionsModalProps {
  closeModal: () => void
}

interface User {
  type: "User"
  tiduuid?: string
  email: string
}

interface Group {
  type: "Group"
  connectGroupId: string
}

interface UserOrGroup {
  key: string
  userOrGroup: User | Group
}

interface DropDownState {
  key: string
  leftPx: number
  topPx: number
}

type AccessLevel = "NoAccess" | "FullAccess" | "ReadOnly"

interface UserOrGroupAccess extends UserOrGroup {
  access: AccessLevel
}

interface NamedUserOrGroup extends UserOrGroup {
  name: string
}

interface NamedUserOrGroupAccess extends UserOrGroupAccess {
  name: string
}

const UserPermissionsModal = (props: UserPermissionsModalProps) => {
  const { closeModal } = props
  const { t } = useTranslation("config")

  const quadriProjectId = useRecoilValue(quadriProjectIdState)
  const connectProjectId = useRecoilValue(projectIdState)
  const projectRegion = useRecoilValue(projectRegionState)

  const [dirty, setDirty] = React.useState(false)
  const [initialized, setInitialized] = React.useState(false)
  const [defaultAccess, _setDefaultAccess] = React.useState("NoAccess")
  const [userOrGroupList, setUserOrGroupList] = React.useState([] as UserOrGroupAccess[])
  const [dropdownState, setDropdownState] = React.useState(null as null | DropDownState) // null or id
  const [, addSnack] = useRecoilState(addSnackSelector)

  const addUserOrGroup = (ug: UserOrGroup) => {
    setDirty(true)
    setUserOrGroupList([...userOrGroupList, { ...ug, access: "FullAccess" }])
  }

  const removeUserOrGroup = (ug: UserOrGroupAccess) => {
    setDirty(true)
    setUserOrGroupList(userOrGroupList.filter((a) => a.key !== ug.key))
  }

  const setAccess = (key: string, access: AccessLevel) => {
    setDirty(true)
    setUserOrGroupList(userOrGroupList.map((ug) => (ug.key === key ? { ...ug, access } : ug)))
  }

  const setDefaultAccess = (access: string) => {
    setDirty(true)
    _setDefaultAccess(access)
  }

  const getProjectAccess = useQuery(GET_PROJECT_ACCESS, {
    fetchPolicy: "no-cache",
    variables: { quadriProjectId, connectProjectId },
    onCompleted: (data) => {
      const response = data.getProjectAccess
      _setDefaultAccess(response.defaultAccess)
      setUserOrGroupList(response.userOrGroupAccess.map(convertGraphQlUserGroupAccess))
      setInitialized(true)
    },
    onError: (error) => addSnack(getLoadErrorSnackProps(t, error.message))
  })

  const [updateProjectAccess, updateResponse] = useMutation(UPDATE_PROJECT_ACCESS, {
    onCompleted: (data) => {
      // setDirty(false)
      addSnack(getSuccessSnackProps(t))
      closeModal()
    },
    onError: (error) => addSnack(getSaveErrorSnackProps(t, error.message))
  })

  const onSubmit = () => {
    updateProjectAccess({
      variables: {
        quadriProjectId,
        request: {
          connectProjectId,
          connectProjectRegion: projectRegion,
          defaultAccess,
          userOrGroupAccess: userOrGroupList.map(convertUserGroupAccessToGraphQl)
        }
      }
    })
  }

  const defaultAccessOptions = accessLevelOptions.map((o) => ({
    ...o,
    label: t(o.labelKey)
  }))

  const dropdownUser = userOrGroupList.find((ug) => ug.key === dropdownState?.key)
  const accessLevel = dropdownUser ? dropdownUser.access : "FullAccess"

  const toggleDropdown = (state: DropDownState) =>
    dropdownState
      ? state.key === dropdownState.key
        ? setDropdownState(null)
        : setDropdownState(state)
      : setDropdownState(state)

  const submitText = t("userPermissionsModal.submit")

  return (
    <Modal
      closeModal={closeModal}
      heading={t("userPermissionsModal.userPermissions")}
      submitButtonText={submitText}
      submitButtonClasses="primary"
      onSubmitButtonClicked={onSubmit}
      submitSpinner={updateResponse.loading}
      submitDisabled={!(dirty && initialized)}
      large={true}
    >
      {dropdownState && <AccessLevelDropdown {...{ dropdownState, accessLevel, setAccess, setDropdownState }} />}
      <CheckRadioGroup
        options={defaultAccessOptions}
        activeOptionId={defaultAccess}
        setActiveOption={setDefaultAccess}
        title={t("userPermissionsModal.defaultAccess")}
        loading={getProjectAccess.loading || !initialized}
      />

      {initialized && <UserOrGroupSelector t={t} addUserOrGroup={addUserOrGroup} userOrGroupList={userOrGroupList} />}

      {userOrGroupList.length > 0 && initialized && (
        <UserOrGroupList
          userOrGroupList={userOrGroupList}
          removeUserOrGroup={removeUserOrGroup}
          toggleDropdown={toggleDropdown}
          loading={getProjectAccess.loading}
        />
      )}
    </Modal>
  )
}

interface CheckRadioGroupProps {
  options: {
    value: string
    label: string
  }[]
  activeOptionId: string
  setActiveOption: (optionId: string) => void
  title?: string
  loading: boolean
}

const CheckRadioGroup = (props: CheckRadioGroupProps) => {
  const { options, activeOptionId, setActiveOption, loading } = props
  const title_bold = props.title!.substring(0, props.title!.indexOf("("))
  let index = props.title!.indexOf("(")
  const title2 = props.title!.slice(index, props.title!.length)
  return loading ? (
    <LoadingSkeleton />
  ) : (
    <div className="input-group checkradio mb-0">
      <label className="d-flex flex-column align-items-start">
        <div className="mb-1" style={{ fontWeight: 700, fontSize: "0.875rem", color: "black" }}>
          {title_bold}
        </div>
        <div>{title2}</div>
      </label>
      {options.map(({ value, label }) => {
        const id = value
        return (
          <div key={value} className={"group-items mr-0"}>
            <div className="icon radio">
              <input
                className="custom-input"
                name={value}
                id={id}
                type="radio"
                value={value}
                checked={value === activeOptionId}
                onChange={() => setActiveOption(value)}
              />
              <label></label>
            </div>
            <label htmlFor={id}>{label}</label>
          </div>
        )
      })}
    </div>
  )
}

const UserOrGroupSelector = (props: {
  t: TFunction<"config", undefined>
  addUserOrGroup: (userOrGroup: UserOrGroup) => void
  userOrGroupList: UserOrGroupAccess[]
}) => {
  const { t, addUserOrGroup, userOrGroupList } = props
  const projectUsers = useRecoilValueLoadable(projectUsersState)
  const groups = useRecoilValueLoadable(userGroupsState)
  const [search, setSearch] = React.useState("")

  const selectUserOrGroup = (ug: UserOrGroup) => {
    setSearch("")
    addUserOrGroup({ ...ug })
  }

  const onUserInputChange = (e: any) => {
    setSearch(e.target.value)
  }

  const autoCompleteList = getAutoCompleteListData(
    projectUsers.contents,
    groups.contents as { id: string; name: string }[],
    search,
    userOrGroupList
  )

  return (
    <>
      {projectUsers.state === "loading" || groups.state === "loading" ? (
        <LoadingSkeleton />
      ) : (
        <div className="input-group tags connect-modal">
          <label
            id="tageditor-title"
            htmlFor="groupOrUserSearchInput"
            style={{ fontWeight: 700, fontSize: "0.875rem", color: "black" }}
          >
            {t("userPermissionsModal.peopleGroupsAccess")}
          </label>
          <div className="group-items input-focus-group">
            <ul className="te-mw-full" style={{ maxWidth: "unset" }}>
              <li key="text-input-with-datalist">
                <input
                  id="groupOrUserSearchInput"
                  type="text"
                  placeholder={t("userPermissionsModal.searchPrompt")}
                  className=""
                  list="usersList"
                  onChange={onUserInputChange}
                  value={search}
                  autoComplete="on"
                />
                <div className="line"></div>

                {typeof autoCompleteList !== "undefined" && (
                  <AutoCompleteList list={autoCompleteList} selectUserOrGroup={selectUserOrGroup} search={search} />
                )}
              </li>
            </ul>
          </div>
        </div>
      )}
    </>
  )
}

const AutoCompleteList = (props: {
  list: NamedUserOrGroup[]
  selectUserOrGroup: (ug: UserOrGroup) => void
  search: string
}) => {
  const { list: autoCompleteList, selectUserOrGroup, search } = props
  const { t } = useTranslation("config")

  return (
    <div className="auto-complete te-top-fix ">
      <ul className="mw-100">
        {autoCompleteList.length > 0 ? (
          autoCompleteList.map((a) => (
            <UserOrGroupAutocompleteListItem
              key={a.key}
              userOrGroup={a}
              selectUserOrGroup={selectUserOrGroup}
              search={search}
            />
          ))
        ) : (
          <li>
            <div className="select-menu-item">
              {t("userPermissionsModal.searchNoResults")}: {search}
            </div>
          </li>
        )}
      </ul>
    </div>
  )
}

const UserOrGroupAutocompleteListItem = (props: {
  userOrGroup: NamedUserOrGroup
  selectUserOrGroup: (ug: UserOrGroup) => void
  search: string
}) => {
  const { userOrGroup, selectUserOrGroup, search } = props
  const onClick = () => selectUserOrGroup(userOrGroup)
  return (
    <li key={userOrGroup.key} onClick={onClick}>
      <div className="select-menu-item ">
        <div className="text-ellipsis small">
          <HighLightMatch text={userOrGroup.name} search={search} />
        </div>
      </div>
    </li>
  )
}

const HighLightMatch = (props: { text: string; search: string }) => {
  const { text, search } = props
  const start = stringMatch(text, search)
  if (start === -1) {
    return <>{text}</>
  } else {
    const end = start + search.length
    const before = text.slice(0, start)
    const match = text.slice(start, end)
    const after = text.slice(end)
    return (
      <>
        {before}
        <b>{match}</b>
        {after}
      </>
    )
  }
}

const UserOrGroupList = (props: {
  userOrGroupList: UserOrGroupAccess[]
  removeUserOrGroup: (ug: UserOrGroupAccess) => void
  toggleDropdown: (state: DropDownState) => void
  loading: boolean
}) => {
  const { t } = useTranslation("config")
  const usersByTid = useRecoilValueLoadable(usersByTidUuidState)
  const groups = useRecoilValueLoadable(userGroupsState)

  if (props.loading || groups.state === "loading" || usersByTid.state === "loading") {
    return <LoadingSkeleton />
  } else if (props.userOrGroupList.length === 0) {
    return null
  } else {
    const namedUserOrGroupAccessList = getNamedUserOrGroupAccessList(
      props.userOrGroupList,
      usersByTid.contents,
      groups.contents
    )
    return (
      <div>
        <h5 className="text-bold mb-1 mt-2">{t("userPermissionsModal.whoHasAccess")}</h5>
        <ul className="list selected-permissions" id="selected-permissions-container">
          {namedUserOrGroupAccessList.map((ug) => (
            <UserOrGroupListItem
              key={ug.key}
              userOrGroup={ug}
              removeUserOrGroup={props.removeUserOrGroup}
              toggleDropdown={props.toggleDropdown}
            />
          ))}
        </ul>
      </div>
    )
  }
}

const UserOrGroupListItem = (props: {
  userOrGroup: NamedUserOrGroupAccess
  removeUserOrGroup: (ug: UserOrGroupAccess) => void
  toggleDropdown: (state: DropDownState) => void
}) => {
  const { userOrGroup, removeUserOrGroup, toggleDropdown } = props
  const { t } = useTranslation("config")
  const ref = React.useRef(null)

  const initials = getInitalFromName(userOrGroup.name)

  const onClickRemove = () => removeUserOrGroup(userOrGroup)
  const onClickAccess = () => {
    const el = ref.current
    const rect = (el as any).getBoundingClientRect()
    toggleDropdown({ key: userOrGroup.key, leftPx: rect.left, topPx: rect.bottom })
  }

  const accessLevel = t(accessLevels[userOrGroup.access].labelKey)

  return (
    <li className="list-item group">
      <div className="group-items px-0">
        <div className="chips non-editable circle mb-0 text-semibold">
          <span>{initials}</span>
        </div>
        <div className="block mr-2">{userOrGroup.name}</div>
        <div className="dropdown-pane-container">
          <div ref={ref} className="name dropdownpane-link text-ellipsis" onClick={onClickAccess}>
            <span className="text-ellipsis">{accessLevel}</span>
            <i className="icon-font tc-icon-arrow-drop-down"></i>
          </div>
          <div className="dropdown-pane w-auto is-open vhidden" id="dropdown-options-dummy-1"></div>
        </div>
        <button className="button tertiary icon-circle icon-medium" onClick={onClickRemove}>
          <i className="icon-font tc-icon-content-close"></i>
        </button>
      </div>
    </li>
  )
}

const AccessLevelDropdown = (props: {
  dropdownState: DropDownState
  accessLevel: AccessLevel
  setDropdownState: (state: null | DropDownState) => void
  setAccess: (key: string, access: AccessLevel) => void
}) => {
  const { dropdownState, accessLevel, setDropdownState, setAccess } = props
  const { key, leftPx, topPx } = dropdownState
  const { t } = useTranslation("config")

  const onClickAccessLevel = (accessLevel: AccessLevel) => {
    setDropdownState(null)
    setAccess(key, accessLevel)
  }

  return (
    <div className="dropdown-pane w-auto is-open" style={{ position: "fixed", left: `${leftPx}px`, top: `${topPx}px` }}>
      <ul className="dropdown-list">
        {accessLevelOptions.map(({ value, labelKey }) => (
          <li
            className={`selectable list-item${accessLevel === value ? " active" : ""}`}
            key={`${value}-li`}
            onClick={() => onClickAccessLevel(value as AccessLevel)}
          >
            {t(labelKey)}
          </li>
        ))}
      </ul>
    </div>
  )
}

const getSuccessSnackProps = (t: any): SnackProps => ({
  type: "success",
  message: t("userPermissionsModal.saveSuccess")
})

const getSaveErrorSnackProps = (t: any, errorMessage?: string): SnackProps => ({
  type: "error",
  message: `${t("userPermissionsModal.saveError")}${errorMessage ? `: ${errorMessage}` : ""}`
})

const getLoadErrorSnackProps = (t: any, errorMessage?: string): SnackProps => ({
  type: "error",
  message: `${t("userPermissionsModal.loadError")}${errorMessage ? `: ${errorMessage}` : ""}`
})

const getAutoCompleteListData = (
  projectUsers: ConnectUser[],
  groups: { id: string; name: string }[],
  search: string,
  userOrGroupList: UserOrGroupAccess[]
): NamedUserOrGroup[] | undefined => {
  console.log("Project connect users: ", projectUsers)

  return search.length === 0
    ? undefined
    : projectUsers
        .filter((user: ConnectUser) => user.status !== "REMOVED")
        .map(
          (user: ConnectUser): NamedUserOrGroup => ({
            key: user.email,
            userOrGroup: {
              type: "User",
              tiduuid: user.tiduuid,
              email: user.email
            },
            name: getUserDisplayName(user)
          })
        )
        .concat(
          groups.map(({ id, name }) => ({ key: id, userOrGroup: { type: "Group", connectGroupId: id, name }, name }))
        )
        .filter((u) => stringMatch(u.name, search) !== -1)
        .filter((u) => !userOrGroupList.find((b) => b.key === u.key))
}

const getUserDisplayName = (user?: ConnectUser) =>
  user ? `${user.firstName} ${user.lastName} (${user.email})` : `<unknown user>`

const getNamedUserOrGroupAccessList = (
  list: UserOrGroupAccess[],
  usersByTid: { [key: string]: ConnectUser },
  groups: { id: string; name: string }[]
): NamedUserOrGroupAccess[] => {
  console.log("user list: ", list)

  return list.map((ug) => ({
    ...ug,
    name:
      ug.userOrGroup.type === "User"
        ? ug.userOrGroup.tiduuid
          ? getUserDisplayName(usersByTid[ug.userOrGroup.tiduuid])
          : ug.userOrGroup.email
        : groups.find((g) => g.id === ug.key)?.name || "<unknown group>"
  }))
}

const accessLevels: { [key: string]: { value: string; labelKey: string } } = {
  FullAccess: { value: "FullAccess", labelKey: "userPermissionsModal.fullAccess" },
  ReadOnly: { value: "ReadOnly", labelKey: "userPermissionsModal.readOnly" },
  NoAccess: { value: "NoAccess", labelKey: "userPermissionsModal.noAccess" }
}

const accessLevelOptions = Object.entries(accessLevels).map(([, e]) => e)

const getInitalFromName = (name: string) => {
  if (name === "") {
    return "XX"
  } else if (name.length === 1) {
    const c = name[0].toUpperCase()
    return `${c}${c}`
  } else {
    const a = name[0].toUpperCase()
    const b = name[1].toUpperCase()
    return `${a}${b}`
  }
}

const stringMatch = (text: string, search: string) => text.toLowerCase().indexOf(search.toLowerCase())

const LoadingSkeleton = () => <Skeleton height={"2rem"} />

const convertGraphQlUserGroupAccess = (ug: {
  type: "User" | "Group"
  id?: string
  email?: string
  access: AccessLevel
}): UserOrGroupAccess => {
  const { type, id, email, access } = ug

  if (type === "User") {
    return {
      key: email!,
      userOrGroup: {
        type: "User",
        tiduuid: id!,
        email: email!
      },
      access
    }
  } else {
    return {
      key: id!,
      userOrGroup: {
        type: "Group",
        connectGroupId: id!
      },
      access
    }
  }
}

const convertUserGroupAccessToGraphQl = (
  uga: UserOrGroupAccess
): {
  type: "User" | "Group"
  id?: string
  email?: string
  access: AccessLevel
} => {
  const ug = uga.userOrGroup
  if (ug.type === "User") {
    return {
      type: "User",
      id: ug.tiduuid,
      email: ug.email,
      access: uga.access
    }
  } else {
    return {
      type: "Group",
      id: ug.connectGroupId,
      access: uga.access
    }
  }
}

export default UserPermissionsModal
